import { create } from 'zustand';
import { api, AdminPayout, AdminStats, PlatformSettings, RevenuePoint } from '../lib/api';

interface AdminStoreState {
  stats: AdminStats | null;
  revenue: RevenuePoint[];
  payouts: AdminPayout[];
  settings: PlatformSettings | null;
  isLoading: boolean;
  error: string | null;

  fetchStats: () => Promise<void>;
  fetchRevenue: (days?: number) => Promise<void>;
  fetchPayouts: () => Promise<void>;
  updatePayoutStatus: (id: number, status: string) => Promise<void>;
  fetchSettings: () => Promise<void>;
  updateSettings: (payload: Partial<PlatformSettings>) => Promise<void>;
}

export const useAdminStore = create<AdminStoreState>((set) => ({
  stats: null,
  revenue: [],
  payouts: [],
  settings: null,
  isLoading: false,
  error: null,

  fetchStats: async () => {
    set({ isLoading: true, error: null });
    try {
      const stats = await api.stats.get();
      set({ stats, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch stats';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchRevenue: async (days = 30) => {
    try {
      const revenue = await api.stats.revenue(days);
      set({ revenue });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch revenue' });
      throw error;
    }
  },

  fetchPayouts: async () => {
    try {
      const payouts = await api.payouts.list();
      set({ payouts });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch payouts' });
      throw error;
    }
  },

  updatePayoutStatus: async (id: number, status: string) => {
    const updated = await api.payouts.updateStatus(id, status);
    set((state) => ({
      payouts: state.payouts.map((p) => (p.id === id ? updated : p)),
    }));
  },

  fetchSettings: async () => {
    try {
      const settings = await api.settings.get();
      set({ settings });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch settings' });
      throw error;
    }
  },

  updateSettings: async (payload) => {
    const settings = await api.settings.update(payload);
    set({ settings });
  },
}));

export function useAdmin() {
  const store = useAdminStore();
  return {
    stats: store.stats,
    revenue: store.revenue,
    payouts: store.payouts,
    settings: store.settings,
    isLoading: store.isLoading,
    error: store.error,
    fetchStats: store.fetchStats,
    fetchRevenue: store.fetchRevenue,
    fetchPayouts: store.fetchPayouts,
    updatePayoutStatus: store.updatePayoutStatus,
    fetchSettings: store.fetchSettings,
    updateSettings: store.updateSettings,
  };
}
